import clsx from 'clsx';

type TextAreaRadius = 'default' | 'square' | 'square-left' | 'square-right';
type TextAreaStyle = 'white' | 'gray';

interface InputProps
  extends Omit<React.HTMLProps<HTMLTextAreaElement>, 'style'> {
  id: string;
  radius?: TextAreaRadius;
  style?: TextAreaStyle;
  label: string;
  name: string;
  rows?: number;
  hasError?: boolean;
  hideLabel?: boolean;
}

const TextArea: React.FC<InputProps> = ({
  radius = 'default',
  style = 'white',
  label,
  name,
  hasError,
  hideLabel = false,
  rows = 4,
  id,
  value,
  className,
  ...otherProps
}) => {
  return (
    <div
      className={clsx(
        'group relative w-full overflow-hidden',
        hideLabel ? '' : 'h-full',
        // Background colors
        style === 'white' && 'bg-white',
        style === 'gray' && 'bg-[#F5F5F5]',
        // Border radius variations
        radius === 'default' && 'rounded-[0.375rem]',
        radius === 'square' && 'rounded-none',
        radius === 'square-left' && 'rounded-r-[0.375rem]',
        radius === 'square-right' && 'rounded-l-[0.375rem]',
        // Focus and error states
        'focus-within:ring-1 focus-within:ring-secondary-main focus-within:ring-offset-0',
        hasError && '!border !border-error-dark',
        className
      )}
    >
      {!hideLabel && (
        <label
          htmlFor={id}
          className={clsx(
            'absolute left-2 top-0 w-full bg-inherit pt-1.5 text-gray-8',
            'transition-colors duration-200',
            value && 'caps-xs',
            !value && 'hidden',
            hasError && '!text-error-dark',
            'group-focus-within:text-secondary-main'
          )}
        >
          {label}
        </label>
      )}
      <textarea
        id={id}
        aria-label={label}
        name={name}
        rows={rows}
        value={value}
        className={clsx(
          'ut-sm no-scrollbar h-full w-full cursor-pointer border-0',
          'text-[1rem] text-black-1',
          'placeholder:font-[var(--font-montserrat)] placeholder:text-gray-8',
          'bg-inherit',
          'outline-none focus:outline-none',
          'focus:ring-0',
          hideLabel
            ? 'p-3'
            : [
              'pb-0 pl-2 pr-0',
              'placeholder:translate-x-1 placeholder:translate-y-0.5',
              value && 'pt-[1.25rem]',
            ]
        )}
        {...otherProps}
      />
    </div>
  );
};

export default TextArea;
